/**
 * Permission keys (plan §10.3), default roles, and the branch-scoped check that runs on
 * the device (to shape the UI) and again on the server (when a command is applied).
 * @module
 */
import { z } from 'zod';

/**
 * The V1 permission keys: plan §10.3, plus `orders.*` for manual orders (M7, WP7.1) and
 * handing them over at a station (WP7.3).
 */
export const PERMISSION_KEYS = [
  'production.plan',
  'production.run',
  'production.complete',
  'receiving.receive',
  'stock.count',
  'stock.adjust',
  'stock.transfer',
  'writeoff.request',
  'writeoff.approve',
  'purchasing.request',
  'purchasing.order',
  'purchasing.approve',
  'catalogue.edit',
  'recipe.edit',
  'recipe.signoff',
  'labels.print',
  'staff.manage',
  'devices.manage',
  'integration.view',
  'integration.go_live',
  'reports.view',
  // Customer orders (M7): reading them; taking, editing and cancelling them.
  'orders.view',
  'orders.edit',
  // Handing an order over at a station (M7, WP7.3): the counter, a physical fact.
  'orders.handover',
] as const;

/** One of {@link PERMISSION_KEYS}. */
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** zod schema for a permission key. */
export const permissionKeySchema = z.enum(PERMISSION_KEYS);

/** True when `value` is a V1 permission key. */
export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === 'string' && (PERMISSION_KEYS as readonly string[]).includes(value);
}

/** Keys of the default roles. */
export type DefaultRoleKey = 'owner' | 'manager' | 'baker' | 'receiving' | 'foh';

/** A named set of permission keys. */
export interface RoleDefinition {
  name: string;
  description: string;
  permissions: readonly PermissionKey[];
}

const allExcept = (...excluded: PermissionKey[]) =>
  PERMISSION_KEYS.filter((k) => !excluded.includes(k));

/**
 * Suggested default roles, seeded into each new account (the account can edit them).
 * `integration.go_live` is owner-only. FOH only reads in V1 (plan: "view only in V1"; D-075); an account can
 * grant it `orders.handover` if the counter hands orders over.
 */
export const DEFAULT_ROLES: Readonly<Record<DefaultRoleKey, RoleDefinition>> = {
  owner: {
    name: 'Owner',
    description: 'Everything, including taking an integration live.',
    permissions: [...PERMISSION_KEYS],
  },
  manager: {
    name: 'Manager',
    description: 'Runs the business day to day: plans, approves, edits the catalogue.',
    permissions: allExcept('integration.go_live'),
  },
  baker: {
    name: 'Baker',
    description: 'Runs and completes production, counts stock, prints labels.',
    permissions: [
      'production.run',
      'production.complete',
      'stock.count',
      'stock.transfer',
      'writeoff.request',
      'purchasing.request',
      'labels.print',
      // Bakers see which orders their batches make, and hand them over in a small shop.
      'orders.view',
      'orders.handover',
    ],
  },
  receiving: {
    name: 'Receiving',
    description: 'Receives deliveries, counts and moves stock, prints labels.',
    permissions: [
      'receiving.receive',
      'stock.count',
      'stock.transfer',
      'writeoff.request',
      'purchasing.request',
      'labels.print',
    ],
  },
  foh: {
    name: 'Front of house',
    description: 'Sees stock and today’s orders.',
    permissions: ['reports.view', 'orders.view'],
  },
};

/**
 * One granted permission, scoped to a branch or to all branches (`branchId: null`).
 * Grants come from role assignments; see {@link grantsFromAssignments}.
 */
export interface PermissionGrant {
  key: PermissionKey;
  branchId: string | null;
}

/** A role given to a staff member or admin user, for one branch or all (`null`). */
export interface RoleAssignment {
  permissions: readonly PermissionKey[];
  branchId: string | null;
}

/** Flattens role assignments into grants (duplicates removed). */
export function grantsFromAssignments(assignments: readonly RoleAssignment[]): PermissionGrant[] {
  const seen = new Set<string>();
  const out: PermissionGrant[] = [];
  for (const a of assignments) {
    for (const key of a.permissions) {
      const id = `${key}\u0000${a.branchId ?? ''}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ key, branchId: a.branchId });
    }
  }
  return out;
}

/**
 * Does someone holding `grants` have `key` for `branchId`?
 * - A grant with `branchId: null` covers every branch.
 * - A branch grant covers only that branch.
 * - For an account-wide action (`branchId: null`, e.g. editing the catalogue) only an
 *   all-branches grant counts.
 */
export function hasPermission(
  grants: readonly PermissionGrant[],
  key: PermissionKey,
  branchId: string | null,
): boolean {
  return grants.some(
    (g) => g.key === key && (g.branchId === null || (branchId !== null && g.branchId === branchId)),
  );
}

// --- who may open what (D-070; B-061, B-062) ----------------------------------------------

/**
 * The areas of the Admin, each gated on the server by one read rule. The Admin's menu,
 * shortcuts and pages ask the same map, so a section the server would refuse is never shown.
 */
export type AdminArea =
  | 'dashboard'
  | 'stock'
  | 'approvals'
  | 'sales'
  | 'production'
  | 'purchasing'
  | 'planning'
  | 'orders'
  | 'reports'
  | 'catalogue'
  | 'recipes'
  | 'imports'
  | 'replay'
  | 'settings';

/** Reading purchasing (prices, supplier contacts, order values): any purchasing permission (D-070). */
export const PURCHASING_READ_PERMISSIONS: readonly PermissionKey[] = [
  'purchasing.request',
  'purchasing.order',
  'purchasing.approve',
];

/**
 * Seeing purchase requests in the approvals inbox: whoever raises or approves them, never
 * `reports.view` alone (D-070).
 */
export const PURCHASE_REQUEST_READ_PERMISSIONS: readonly PermissionKey[] = [
  'purchasing.request',
  'purchasing.approve',
];

/** Seeing the stock approvals (counts, write-offs, corrections) in the inbox. */
export const STOCK_APPROVAL_READ_PERMISSIONS: readonly PermissionKey[] = ['reports.view'];

/**
 * Any one of these opens the area; `null`: every signed-in admin (the dashboard; the
 * catalogue reads, which need no permission).
 */
export const ADMIN_AREA_READ: Readonly<Record<AdminArea, readonly PermissionKey[] | null>> = {
  dashboard: null,
  stock: ['reports.view'],
  approvals: [...STOCK_APPROVAL_READ_PERMISSIONS, ...PURCHASE_REQUEST_READ_PERMISSIONS],
  sales: ['reports.view'],
  production: ['reports.view'],
  purchasing: PURCHASING_READ_PERMISSIONS,
  planning: ['production.plan', 'reports.view'],
  orders: ['orders.view', 'orders.edit'],
  reports: ['reports.view'],
  catalogue: null,
  recipes: ['reports.view'],
  imports: ['integration.view'],
  replay: ['reports.view'],
  // Stations and held or refused actions (`devices.manage`); approval limits (`reports.view`);
  // staff, their station PINs and roles (`staff.manage`, B-080).
  settings: ['devices.manage', 'reports.view', 'staff.manage'],
};

/**
 * Staff, station PINs and roles (Settings → Staff, Settings → Roles; B-080). Owner and
 * Manager hold it by default. Changing a role also needs all branches (roles are
 * account-wide); a role that includes {@link OWNER_PERMISSION} only an owner may change.
 */
export const STAFF_MANAGE_PERMISSION: PermissionKey = 'staff.manage';

/**
 * What makes someone an owner: taking an integration live, the one owner-only key. Only
 * someone holding it may grant it, take it away, or give a role that includes it; and the
 * account always keeps at least one Admin member who holds it for all branches.
 */
export const OWNER_PERMISSION: PermissionKey = 'integration.go_live';

/** True when someone holding `permissions` holds at least one of `keys` (`null`: anyone). */
export function holdsAny(
  permissions: readonly string[],
  keys: readonly PermissionKey[] | null,
): boolean {
  return keys === null || keys.some((k) => permissions.includes(k));
}

/** Whether someone holding `permissions` may open `area` of the Admin. */
export function canOpenArea(permissions: readonly string[], area: AdminArea): boolean {
  return holdsAny(permissions, ADMIN_AREA_READ[area]);
}

/** Which kinds of approval request someone sees in the inbox (D-070). */
export interface ApprovalVisibility {
  /** Counts, write-offs and corrections. */
  stock: boolean;
  /** Purchase requests (and their values). */
  purchase: boolean;
}

/** The approval requests someone holding `permissions` sees. */
export function approvalVisibility(permissions: readonly string[]): ApprovalVisibility {
  return {
    stock: holdsAny(permissions, STOCK_APPROVAL_READ_PERMISSIONS),
    purchase: holdsAny(permissions, PURCHASE_REQUEST_READ_PERMISSIONS),
  };
}
